import React, { useCallback, useEffect, useState } from 'react'
import { Outlet, useNavigate } from 'react-router-dom'
import classNames from 'classnames'

import Container from 'components/common/Container'
import FeatureDisabled from 'components/FeatureDisabled'
import IdleStage from 'components/stages/video/IdleStage'
import ProcessingStage from 'components/stages/video/ProcessingStage'
import {
  changeCurrentAssetIndex,
  changeProcessedAssets,
  changeStage,
} from 'store/reducers/stageReducer'
import Processor, { VideoStrategy } from 'api/Retomagic'
import useFetchStyles from 'hooks/query/useFetchStyles'
import useFetchSuggestions from 'hooks/query/useFetchSuggestions'
import { useAppDispatch, useAppSelector } from 'hooks/store'
import {
  ProcessedAssetsChangedPayload,
  ProcessorEvents,
} from 'types/ProcessorEvents'
import useFetchRequestMyProfile from '../../hooks/query/useFetchRequestMyProfile'
import GenerationContainer, {
  AsideSlot,
  MainSlot,
} from '../GenerationControls/GenerationContainer/GenerationContainer'
import UnPaidSubscriptionContent from '../UnPaidSubscriptionContent'

import c from './VideoArt.module.scss'

function VideoArt() {
  const dispatch = useAppDispatch()

  const { stage, processedAssets, currentAssetIndex } = useAppSelector(
    (state) => state.stage,
  )

  const navigate = useNavigate()

  const [generationProcessor, setGenerationProcessor] =
    useState<Processor | null>(null)

  const { data: stylesData } = useFetchStyles()
  const [seconds, setSeconds] = useState<number | undefined>(0)

  const { data: suggestionsData } = useFetchSuggestions()

  const [chosenStyle, setChosenStyle] = useState<string | undefined>('')

  const [text, setText] = useState<string>('')

  const { data: user } = useFetchRequestMyProfile()

  const handleProcessedAssetsChanged = useCallback(
    (payload: ProcessedAssetsChangedPayload) => {
      const { assets, isFirstSucceededResult, id } = payload

      dispatch(changeProcessedAssets(assets))
      dispatch(changeStage('idle'))

      if (isFirstSucceededResult) {
        navigate(`/video-art/finish/${id}`)
      }
    },
    [],
  )

  const handleProcessingStart = useCallback(async () => {
    try {
      dispatch(changeStage('processing'))

      const thisStyle = stylesData.find((elem) => elem.id === chosenStyle)

      const strategy = new VideoStrategy(text)

      const processor = new Processor(strategy)
      processor.on(
        ProcessorEvents.PROCESSED_ASSETS_CHANGED,
        handleProcessedAssetsChanged,
      )

      await processor.start()
      setSeconds(strategy.getSeconds())
      setGenerationProcessor(processor)
    } catch (error) {
      console.log(error)
    }
  }, [text, handleProcessedAssetsChanged, chosenStyle, stylesData])

  useEffect(() => {
    // Сброс данных, если пользователь вернулся на главный экран
    if (stage !== 'idle') return
    setText('')
    dispatch(changeProcessedAssets([]))
    dispatch(changeCurrentAssetIndex(0))
  }, [stage])

  const onExampleClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      setText(event.currentTarget?.value)
    },
    [],
  )

  const onStyleClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      if (chosenStyle === event.currentTarget.value) {
        setChosenStyle(undefined)
      } else {
        setChosenStyle(event.currentTarget.value)
      }
    },
    [chosenStyle],
  )

  useEffect(() => {
    return () => {
      if (stage === 'processing') {
        dispatch(changeProcessedAssets([]))
        dispatch(changeCurrentAssetIndex(0))
        setText('')
        generationProcessor?.stop()
        setGenerationProcessor(null)
        dispatch(changeStage('idle'))
      }
    }
  }, [generationProcessor])

  const isAdminOrJournalist =
    user?.role !== 'admin' && user?.role !== 'journalist'

  return (
    <GenerationContainer stage={stage}>
      <MainSlot>
        {isAdminOrJournalist && <FeatureDisabled />}
        {stage === 'processing' && (
          <div className={c.processing}>
            <ProcessingStage seconds={seconds!} />
          </div>
        )}
        <Outlet />
      </MainSlot>
      <AsideSlot>
        <IdleStage
          text={text}
          onTextChange={setText}
          onProcessingStart={handleProcessingStart}
          onExampleClick={onExampleClick}
          onStyleClick={onStyleClick}
          chosenStyle={chosenStyle}
          suggestions={suggestionsData}
          styles={stylesData}
        />
      </AsideSlot>
    </GenerationContainer>
  )
}

export default VideoArt
